import { auth } from '../firebase/config';
import { User } from 'firebase/auth';
import { ApiResponse } from '../types';
import { createError, getErrorMessage } from '../utils/errors';

class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || '';
    if (!this.baseUrl) {
      console.warn('REACT_APP_API_URL is not set');
    }
  }

  private async getAuthToken(): Promise<string | null> {
    try {
      const user: User | null = auth.currentUser;
      if (!user) {
        return null;
      }
      return await user.getIdToken();
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }

  private async refreshToken(): Promise<string | null> {
    try {
      const user: User | null = auth.currentUser;
      if (!user) {
        return null;
      }
      return await user.getIdToken(true);
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const token = await this.getAuthToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const url = `${this.baseUrl}${endpoint}`;
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status === 401) {
        const refreshedToken = await this.refreshToken();
        if (refreshedToken) {
          headers['Authorization'] = `Bearer ${refreshedToken}`;
          const retryResponse = await fetch(url, {
            ...options,
            headers,
          });
          return this.handleResponse<T>(retryResponse);
        }
        return {
          error: createError('Authentication failed', 'AUTH_ERROR', 401),
        };
      }

      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        error: createError(
          getErrorMessage(error),
          'NETWORK_ERROR'
        ),
      };
    }
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const text = await response.text();

    if (!response.ok) {
      let message = `HTTP ${response.status}: ${response.statusText}`;
      let code: string | undefined;
      const trimmed = text.trim();
      if (trimmed) {
        try {
          const parsed = JSON.parse(trimmed) as { message?: string; code?: string };
          if (typeof parsed.message === 'string') {
            message = parsed.message;
          }
          if (typeof parsed.code === 'string') {
            code = parsed.code;
          }
        } catch {
          // keep default message
        }
      }
      return {
        error: createError(message, code, response.status),
      };
    }

    const trimmed = text.trim();
    if (!trimmed) {
      return {};
    }

    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed === null) {
        return {};
      }
      return { data: parsed as T };
    } catch {
      return {
        error: createError(
          'Invalid JSON response',
          'INVALID_JSON',
          response.status
        ),
      };
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();
export default apiClient;

